import { useEffect, useRef } from "react"

import { useAppDispatch, useAppSelector } from "shared/config/store"
import { getSmallPreviewLink } from "shared/utils/filesUtils"
import styled from "styled-components"
import { CloseOutlined } from "shared/ui"
import { getUploadingFilesSelector } from 'features/files-uploading/selectors/filesUploadingSelectors'
import { Progress } from 'antd'
import { removeFileFromUploadingThunk, UPLOADER_LIST_TYPES } from 'features/files-uploading/model/slice/filesUploadingSlice'
import { useParams } from "react-router-dom"

import { selectAttachments } from "../model/selectors/aiAssistantSelectors"
import { aiAssistantSlice } from "../model/slice/aiAssistantSlice"

import { AttachmentPreviewIcon } from "./AttachmentPreviewPlaceholder"

export const WriterAttachments = () => {
  const dispatch = useAppDispatch()

  const attachments = useAppSelector(selectAttachments)
  const { chatId } = useParams<{ chatId: string }>()
  const uploadingFiles = getUploadingFilesSelector()
    .filter(f => f.uploadingSourceType === UPLOADER_LIST_TYPES.AI_ASSISTANT_ATTACHMENTS)

  const uploadingFilesRef = useRef<any[]>(uploadingFiles)

  uploadingFilesRef.current = uploadingFiles

  const handleRemoveUploadingFile = (file) => {
    if (file.previewUrl) {
      try {
        URL.revokeObjectURL(file.previewUrl)
      }
      catch { }
    }

    dispatch(removeFileFromUploadingThunk({
      id: file.id,
      name: file.name,
      size: typeof file.size === 'string' ? parseInt(file.size, 10) || 0 : 0,
      type: file.type,
      state: file.state
    }))
  }

  useEffect(() => {
    uploadingFilesRef.current.forEach(file => {
      if (file.previewUrl) {
        URL.revokeObjectURL(file.previewUrl)
      }

      dispatch(removeFileFromUploadingThunk({
        id: file.id,
        name: file.name,
        size: typeof file.size === 'string' ? parseInt(file.size, 10) || 0 : 0,
        type: file.type,
        state: file.state
      }))
    })

    dispatch(aiAssistantSlice.actions.clearAttachments())
  }, [chatId])

  if (attachments.length === 0 && uploadingFiles.length === 0) {
    return null
  }

  return (
    <SAttachmentsRow>
      {attachments.map(attachment => (
        <SAttachmentContainer key={attachment.id}>
          {!!attachment?._embedded?.previews?.sizes?.small && <SAttachmentImage src={getSmallPreviewLink(attachment)} />}

          {!attachment?._embedded?.previews?.sizes?.small && (
            <SAttachmentPlaceholder>
              <SAttachmentIcon type={attachment?.file_info?.mime} />
            </SAttachmentPlaceholder>
          )}

          <SAttachmentRemoveButton onClick={() => dispatch(aiAssistantSlice.actions.removeAttachment(attachment.id))}>
            <SAttachmentRemoveIcon />
          </SAttachmentRemoveButton>
        </SAttachmentContainer>
      ))}

      {uploadingFiles.map(file => (
        <SAttachmentContainer key={file.uploadId || `${file.name}-${file.size}`}>
          {file.previewUrl && file.uploadId && (
            <SAttachmentImage src={file.previewUrl} />
          )}

          {!file.previewUrl && file.uploadId && (
            <SAttachmentPlaceholder>
              <SAttachmentIcon type={file.type} />
            </SAttachmentPlaceholder>
          )}

          <SAttachmentRemoveButton onClick={() => handleRemoveUploadingFile(file)}>
            <SAttachmentRemoveIcon />
          </SAttachmentRemoveButton>

          <SProgress
            percent={file.state.progress}
            size={24}
            showInfo={false}
            type="circle"
            strokeColor="#8747DF"
          />
        </SAttachmentContainer>
      ))}
    </SAttachmentsRow>
  )
}

const SAttachmentsRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  padding: 16px 16px 0;
`

const SAttachmentContainer = styled.div`
  width: 88px;
  height: 88px;
  border-radius: 12px;
  position: relative;
`

const SAttachmentImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 12px;
`

const SAttachmentPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #FEFEFE;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
`

const SAttachmentRemoveButton = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: #FEFEFE;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 6px;
  right: 6px;
  cursor: pointer;
  z-index: 1;
  font-size: 14px;
  color: #000000E0;
`

const SAttachmentRemoveIcon = styled(CloseOutlined)`

`

const SProgress = styled(Progress)`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
`

const SAttachmentIcon = styled(AttachmentPreviewIcon)`
  color: #00000073;
`